/**
 * The term calendar: which teaching week a date falls in, and which dates a week covers.
 *
 * Pure, and in calendar dates only. Every date is a `YYYY-MM-DD` string and all arithmetic
 * is done on UTC midnights, so a week is seven *days* whatever the clocks do: the
 * Europe/Berlin switch to winter time on the last Sunday of October falls in week 0 of the
 * winter term, and arithmetic on local instants made that week an hour short and put its
 * last evening into week 1. The only place an instant becomes a date is `berlinDate`.
 *
 * Week numbers are *teaching* weeks, the way the course numbers its tutorials: week 0 is
 * the week of `start` (2026: `tutorial/planning/plan0.typ` is tutorial 0, dated
 * 20 Oct 2026 — the `start-date` of `headers/config.typ`), and the tutorial sheets of
 * every past year are labelled `0`, `A`, `B`, … in the same order, so `A` is week 1
 * (`sheetLabelForWeek`). A week without tutorials — the Christmas break — is a
 * *skipped calendar slot* and gets no number: the first tutorial after the break is the
 * next week number, as it is the next sheet letter. Numbering the slots instead would
 * make "the same week last year" compare sheet J with sheet L.
 *
 *   slot      0      1     …   8      9       10      11    …
 *   dates     20 Oct 27 Oct    15 Dec 22 Dec  29 Dec  5 Jan
 *   week      W0     W1        W8     (break) (break) W9
 */

/** One term's calendar, as stored (table `terms`) and edited on the Admin page. */
export interface TermCalendar {
  /** the term, by the year it starts in: `2026` is the winter term 2026/27 */
  year: string;
  /** first day of week 0, `YYYY-MM-DD` */
  start: string;
  /** number of teaching weeks, W0 … W(weeks−1) */
  weeks: number;
  /**
   * Calendar slots without tutorials, counted in weeks from `start` (slot 0 is the week of
   * `start`). Not week numbers: a skipped slot has none (see the top of this file).
   */
  skipped: number[];
  /** public holidays, `YYYY-MM-DD`; shown on their week, they do not skip it */
  holidays: string[];
}

/** One calendar slot of a term, as the term routes compute it. */
export interface TermSlot {
  /** weeks after `start` */
  slot: number;
  /** the teaching week, or null for a skipped slot */
  week: number | null;
  /** first and last day, inclusive */
  from: string;
  to: string;
  skipped: boolean;
  /** the holidays that fall into this slot */
  holidays: string[];
}

/** Highest week number a sheet may carry (`meta.week`); see `sanitizeMetaValue`. */
export const MAX_WEEK = 20;
/** Teaching weeks of a term when nobody said otherwise: 0, A … N, as 2018–2025 had. */
export const DEFAULT_TERM_WEEKS = 15;
/** Calendar slots a term may span, teaching weeks and skipped ones together. */
export const MAX_TERM_SLOTS = 40;

/* ---------------------------------------------------------------- dates */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Whether `s` is a real calendar date written `YYYY-MM-DD` (no 30 February). */
export function isDate(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}

const dayNumber = (date: string): number => {
  const m = DATE_RE.exec(date)!;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
};

const fromDayNumber = (n: number): string => new Date(n * DAY_MS).toISOString().slice(0, 10);

/** `date` plus `days` calendar days. */
export function addDays(date: string, days: number): string {
  return fromDayNumber(dayNumber(date) + days);
}

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export function daysBetween(a: string, b: string): number {
  return dayNumber(b) - dayNumber(a);
}

/**
 * The calendar date an instant falls on in Europe/Berlin, where the course runs — not in
 * the server's zone, which is UTC in the container: just after midnight in Berlin is still
 * the previous day in UTC, and the week chip would flip an hour (two in summer) late.
 */
export function berlinDate(instant: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/** The term a date belongs to: September 2026 to August 2027 is term `2026`. */
export function termYearOf(date: string): string {
  const y = Number(date.slice(0, 4));
  /*
   * From April on, the coming winter term: the summer is when its sheets are prepared, and
   * filing them under the term that ended in March made them "handed out" (frozen) at once.
   */
  return String(Number(date.slice(5, 7)) >= 4 ? y : y - 1);
}

/* ---------------------------------------------------------------- weeks */

/** `W3` — how a week is written on chips and in lists. */
export function weekLabel(week: number): string {
  return `W${week}`;
}

const SHEET_LETTERS = '0ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The sheet number the course gives the sheet of a week: `0` for week 0, `A` for week 1, …
 * The tutorial sheets of 2017–2025 are numbered this way (2016 and 2020–2021 had no
 * sheet 0 and start at `A` = week 1 all the same).
 */
export function sheetLabelForWeek(week: number): string | null {
  return Number.isInteger(week) && week >= 0 && week < SHEET_LETTERS.length ? SHEET_LETTERS[week]! : null;
}

/** The inverse of `sheetLabelForWeek`: `0` → 0, `A` → 1, `c` → 3; anything else null. */
export function weekForSheetLabel(label: string): number | null {
  const s = label.trim().toUpperCase();
  if (s.length !== 1) return null;
  const i = SHEET_LETTERS.indexOf(s);
  return i >= 0 ? i : null;
}

/**
 * Every calendar slot of the term, skipped ones included, up to the last teaching week.
 * Skipped slots after the last teaching week are not part of the term and are left out.
 */
export function termSlots(cal: TermCalendar): TermSlot[] {
  const skipped = new Set(cal.skipped);
  const holidays = [...new Set(cal.holidays.filter(isDate))].sort();
  const out: TermSlot[] = [];
  let week = 0;
  for (let slot = 0; week < cal.weeks && slot < MAX_TERM_SLOTS; slot++) {
    const from = addDays(cal.start, slot * 7);
    const to = addDays(from, 6);
    const isSkipped = skipped.has(slot);
    out.push({
      slot,
      week: isSkipped ? null : week,
      from,
      to,
      skipped: isSkipped,
      holidays: holidays.filter((h) => h >= from && h <= to),
    });
    if (!isSkipped) week++;
  }
  return out;
}

/** First and last day of teaching week `week`, or null when the term has no such week. */
export function weekDates(cal: TermCalendar, week: number): { from: string; to: string } | null {
  const slot = termSlots(cal).find((s) => s.week === week);
  return slot ? { from: slot.from, to: slot.to } : null;
}

/** The teaching week `date` falls in; null before the term, after it, and in a break. */
export function weekOf(cal: TermCalendar, date: string): number | null {
  if (!isDate(date) || !isDate(cal.start)) return null;
  const offset = daysBetween(cal.start, date);
  if (offset < 0) return null;
  const slot = termSlots(cal)[Math.floor(offset / 7)];
  return slot ? slot.week : null;
}

/**
 * The week a tutor is preparing on `today`: the week it is, or — in a break, or before
 * the term — the next one with tutorials. Null once the last week is over.
 */
export function currentWeek(cal: TermCalendar, today: string): number | null {
  if (!isDate(today) || !isDate(cal.start)) return null;
  const slots = termSlots(cal);
  for (const s of slots) {
    if (s.to < today) continue;
    if (s.week !== null) return s.week;
  }
  return null;
}

/* ------------------------------------------------------------- defaults */

/**
 * Default skipped slots: every slot that holds Christmas Day or New Year's Day — the
 * university's Christmas break, which in 2026 is the slots of 22 Dec and 29 Dec. The
 * holidays elsewhere (1 Nov, 6 Jan) take out one day, not the week's tutorials.
 */
export function defaultSkipped(start: string, weeks: number): number[] {
  const out: number[] = [];
  let week = 0;
  for (let slot = 0; week < weeks && slot < MAX_TERM_SLOTS; slot++) {
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, slot * 7 + i).slice(5));
    const breakDay = days.includes('12-25') || days.includes('01-01');
    if (breakDay) out.push(slot);
    else week++;
  }
  return out;
}

/**
 * What `headers/config.typ` of the course repository says: `start-date` and
 * `public-holidays`, each a Typst `datetime(day:, month:, year:)` with its arguments in
 * any order. Tolerant: a missing or malformed part is simply absent from the result, and
 * comments are ignored.
 */
export function parseCourseConfig(source: string): Partial<TermCalendar> {
  const text = source
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .split('\n')
    .map((line) => line.replace(/\/\/.*$/, ''))
    .join('\n');
  const datetime = (args: string): string | null => {
    const part = (name: string) => new RegExp(`\\b${name}\\s*:\\s*(\\d+)`).exec(args)?.[1];
    const [d, m, y] = [part('day'), part('month'), part('year')];
    if (!d || !m || !y) return null;
    const date = `${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    return isDate(date) ? date : null;
  };
  const out: Partial<TermCalendar> = {};
  const start = /#let\s+start-date\s*=\s*datetime\s*\(([^)]*)\)/.exec(text);
  const startDate = start ? datetime(start[1]!) : null;
  if (startDate) {
    out.start = startDate;
    out.year = termYearOf(startDate);
  }
  const holidaysAt = /#let\s+public-holidays\s*=\s*\(/.exec(text);
  if (holidaysAt) {
    // up to the bracket that closes the list
    let depth = 1;
    let i = holidaysAt.index + holidaysAt[0].length;
    const from = i;
    for (; i < text.length && depth > 0; i++) {
      if (text[i] === '(') depth++;
      else if (text[i] === ')') depth--;
    }
    const body = text.slice(from, i - 1);
    const dates = [...body.matchAll(/datetime\s*\(([^)]*)\)/g)]
      .map((m) => datetime(m[1]!))
      .filter((d): d is string => Boolean(d));
    out.holidays = [...new Set(dates)].sort();
  }
  return out;
}

/**
 * A complete calendar from what the course config says: `DEFAULT_TERM_WEEKS` teaching
 * weeks with the Christmas slots skipped. Null without a start date.
 */
export function calendarFromConfig(config: Partial<TermCalendar>, weeks = DEFAULT_TERM_WEEKS): TermCalendar | null {
  if (!config.start || !isDate(config.start)) return null;
  return {
    year: config.year ?? termYearOf(config.start),
    start: config.start,
    weeks,
    skipped: defaultSkipped(config.start, weeks),
    holidays: config.holidays ?? [],
  };
}

/**
 * Check a calendar sent by a client. Returns the cleaned calendar (skipped and holidays
 * de-duplicated and sorted) or what is wrong with it, in words for the Admin page.
 */
export function validateCalendar(year: string, input: unknown): TermCalendar | string {
  if (!/^\d{4}$/.test(year)) return 'The term is a year like 2026.';
  const raw = (input ?? {}) as Record<string, unknown>;
  if (!isDate(raw.start)) return 'The start date must be a date (YYYY-MM-DD).';
  if (termYearOf(raw.start) !== year) return `The start date ${raw.start} is not in the term ${year}/${Number(year) + 1}.`;
  const weeks = raw.weeks;
  if (typeof weeks !== 'number' || !Number.isInteger(weeks) || weeks < 1 || weeks > MAX_WEEK + 1) {
    return `The number of weeks must be a whole number from 1 to ${MAX_WEEK + 1}.`;
  }
  const skippedIn = raw.skipped ?? [];
  if (!Array.isArray(skippedIn) || skippedIn.some((n) => typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n >= MAX_TERM_SLOTS)) {
    return `Skipped weeks are calendar weeks counted from the start, 0 to ${MAX_TERM_SLOTS - 1}.`;
  }
  const skipped = [...new Set(skippedIn as number[])].sort((a, b) => a - b);
  // the slots run out before the weeks do when nearly every slot is skipped
  if (termSlots({ year, start: raw.start, weeks, skipped, holidays: [] }).filter((s) => s.week !== null).length < weeks) {
    return `A term spans at most ${MAX_TERM_SLOTS} calendar weeks.`;
  }
  const holidaysIn = raw.holidays ?? [];
  if (!Array.isArray(holidaysIn) || holidaysIn.length > 60 || holidaysIn.some((d) => !isDate(d))) {
    return 'Holidays must be dates (YYYY-MM-DD).';
  }
  const holidays = [...new Set(holidaysIn as string[])].sort();
  return { year, start: raw.start, weeks, skipped, holidays };
}
